// ANYCAST — browser entry point. Wires the planet (WebGL globe), the ring
// sequencer, the synth, the network and the HUD together.

import { LANES, barIndex, type Harmony } from "../shared/music";
import { sanitizeName, type PlayerInfo, type ServerHome } from "../shared/protocol";
import { subsolarPoint, type LatLon } from "../shared/geo";
import { AudioEngine } from "./audio/engine";
import { Conductor } from "./conductor";
import { Globe, type GlobePlayer } from "./globe/globe";
import { Hud } from "./hud";
import { Labels, escapeHtml } from "./labels";
import { Net, localNow } from "./net";
import { downlink, route, theoreticalRttMs, uplink, coloPoint, type Route } from "./paths";
import { RingView, type RingHit } from "./ring";
import { Room } from "./room";
import { captureMoment, fetchConstellation, loadSky, paintSkyConstellations } from "./sky";

// ───────────────────────────── routing ─────────────────────────────

type AppRoute = { kind: "live"; room: string } | { kind: "constellation"; id: string };

function parseRoute(): AppRoute {
  const parts = location.pathname.split("/").filter(Boolean);
  if (parts[0] === "c" && parts[1]) return { kind: "constellation", id: parts[1] };
  if (parts[0] === "r" && parts[1] && /^[a-z0-9][a-z0-9-]{0,31}$/.test(parts[1].toLowerCase())) {
    return { kind: "live", room: parts[1].toLowerCase() };
  }
  return { kind: "live", room: "earth" };
}

const appRoute = parseRoute();
const params = new URLSearchParams(location.search);
const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

// ───────────────────────────── core objects ─────────────────────────────

const globeCanvas = $<HTMLCanvasElement>("globe");
const ringCanvas = $<HTMLCanvasElement>("ring");
const globe = new Globe(globeCanvas, { attachPointerHandlers: false });
const ring = new RingView(ringCanvas);
const engine = new AudioEngine();
const room = new Room();
const hud = new Hud();
const labels = new Labels($("labels"));
let net: Net | null = null;
let playbackMode = false;

const conductor = new Conductor(engine, {
  serverNow: () => (net ? net.serverNow() : localNow()),
  epoch: () => room.epoch,
  cellsAtStep: (step) => room.cellsAtStep(step),
  spatial: (lat, lon) => {
    const p = globe.project(lat, lon);
    const pan = Math.max(-1, Math.min(1, (p.x - layoutState.cx) / Math.max(1, layoutState.globeR))) * 0.8;
    return { pan, facing: p.facing };
  },
  percussionIntensity: () => {
    const n = room.cells.size;
    if (n < 3) return 0;
    return Math.min(0.85, 0.22 + n / 48 + room.humans().length * 0.04);
  },
  droneLevel: () => 1 - Math.min(0.6, room.cells.size / 40),
  onNote: (cell) => {
    ring.flash(cell.step, cell.lane, 1);
    globe.pulse(cell.lat, cell.lon, cell.hue, 0.55);
    labels.touch(cell.owner);
  },
  onStep: () => undefined,
  onBar: (bar, h) => onBar(bar, h),
});

// ───────────────────────────── layout ─────────────────────────────

const layoutState = { cx: 0, cy: 0, globeR: 100, r0: 110, r1: 200, globeMode: false, t: 0 };

function computeLayout(globeMode: boolean) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const mobile = w < 860;
  const top = mobile ? 96 : 24;
  const bottom = mobile ? 150 : 118;
  const availH = Math.max(200, h - top - bottom);
  const size = Math.min(w - (mobile ? 16 : 40), availH);
  const outer = size / 2 - (mobile ? 6 : 18);
  const cx = w / 2;
  const cy = top + availH / 2;
  if (globeMode) {
    const r = outer * 1.02;
    return { cx, cy, globeR: r, r0: r * 1.05, r1: r * 1.4 };
  }
  const r1 = outer;
  const r0 = outer * (mobile ? 0.56 : 0.62);
  return { cx, cy, globeR: r0 * 0.9, r0, r1 };
}

function applyLayout(): void {
  const a = computeLayout(false);
  const b = computeLayout(true);
  const t = layoutState.t;
  const ease = t * t * (3 - 2 * t);
  const mix = (x: number, y: number) => x + (y - x) * ease;
  layoutState.cx = mix(a.cx, b.cx);
  layoutState.cy = mix(a.cy, b.cy);
  layoutState.globeR = mix(a.globeR, b.globeR);
  layoutState.r0 = a.r0;
  layoutState.r1 = a.r1;
  globe.setViewport(layoutState.cx, layoutState.cy, layoutState.globeR);
  ring.layout(a.cx, a.cy, a.r0, a.r1);
}

function onResize(): void {
  ring.resize(window.innerWidth, window.innerHeight);
  globe.resize();
  applyLayout();
}
window.addEventListener("resize", onResize);
onResize();

function setGlobeMode(on: boolean): void {
  layoutState.globeMode = on;
  ring.setVisible(!on);
  document.body.classList.toggle("globe-mode", on);
  $("btn-globe").setAttribute("aria-pressed", String(on));
}

// ───────────────────────────── visual network traffic ─────────────────────────────

function sendRoute(r: Route, hue: number, width = 1, onArrive?: () => void): void {
  if (r.points.length < 2) {
    onArrive?.();
    return;
  }
  globe.sendPacket(r.points, hue, r.legs.map((l) => l.visualMs), { width, onArrive });
}

function pathLabel(p: PlayerInfo, home: ServerHome): string {
  const r = uplink(p, home);
  return `<span class="path">${escapeHtml(p.city || "?")} → <b>${p.colo}</b> → <b>${home.colo}</b> · ${r.realMs.toFixed(1)} ms of light</span>`;
}

/**
 * Animates a message from `origin` to the Durable Object, then fans it out to every
 * listener. `onReachMe` fires when the light arrives at this browser.
 */
function broadcastVisual(origin: Pick<PlayerInfo, "id" | "lat" | "lon" | "colo" | "hue">, onReachMe?: () => void): void {
  const home = room.home;
  const you = room.you;
  if (!home) return;
  const up = uplink(origin, home);
  sendRoute(up, origin.hue, 1.2, () => {
    globe.pulse(home.lat, home.lon, 28, 0.7);
    const listeners = [...room.players.values()].filter((p) => !p.ghost && p.id !== origin.id);
    let delivered = false;
    for (const p of listeners.slice(0, 40)) {
      const isMe = p.id === you?.id;
      sendRoute(downlink(home, p), origin.hue, isMe ? 1.4 : 0.8, () => {
        globe.pulse(p.lat, p.lon, origin.hue, isMe ? 0.9 : 0.4);
        if (isMe) {
          delivered = true;
          onReachMe?.();
        }
      });
    }
    if (!delivered && !listeners.some((p) => p.id === you?.id)) onReachMe?.();
  });
}

// ───────────────────────────── room events ─────────────────────────────

function globePlayers(): GlobePlayer[] {
  return [...room.players.values()].map((p) => ({
    id: p.id,
    lat: p.lat,
    lon: p.lon,
    hue: p.hue,
    ghost: p.ghost,
    isYou: p.id === room.you?.id,
    colo: p.colo,
  }));
}

function refreshPlayers(): void {
  globe.setPlayers(globePlayers());
  const colos = new Set<string>();
  for (const p of room.players.values()) colos.add(p.colo);
  if (room.home) colos.add(room.home.colo);
  globe.setActiveColos([...colos]);
  labels.sync([...room.players.values()], room.you?.id ?? null, room.home);
  if (room.stats) hud.setStats(room.stats, room.humans().length);
}

room.on("welcome", () => {
  if (!room.you) return;
  globe.setHome(room.home ? { lat: room.home.lat, lon: room.home.lon } : null);
  hud.setRoom(room.name);
  hud.setYou(room.you, room.home);
  refreshPlayers();
  const saved = safeStorage.get("anycast.name");
  if (saved && saved !== room.you.name && !playbackMode) net?.send({ t: "rename", name: saved });
  if (!playbackMode && !introDone) globe.focus(room.you.lat, room.you.lon - 25, 2400);
  const others = room.humans().length - 1;
  $("intro-eyebrow").textContent =
    others > 0 ? `${others} ${others === 1 ? "person is" : "people are"} playing right now` : "live now · the ghosts are playing";
  conductor.start();
});

room.on("players", refreshPlayers);

room.on("join", (p) => {
  if (p.id === room.you?.id) return;
  if (!p.ghost && room.home) {
    hud.feedItem(`<span class="who">${escapeHtml(p.name)}</span> joined from ${escapeHtml(p.city || p.country)} ${pathLabel(p, room.home)}`, p.hue);
    sendRoute(uplink(p, room.home), p.hue, 1.5);
  } else if (p.ghost && room.home) {
    hud.feedItem(`<span class="who">${escapeHtml(p.name)}</span> drifts in <span class="path">at <b>${p.colo}</b></span>`, p.hue);
    sendRoute(route([coloPoint(p.colo, p), room.home]), p.hue, 0.8);
  }
});

room.on("leave", (p) => {
  if (!p.ghost) hud.feedItem(`<span class="who">${escapeHtml(p.name)}</span> left ${escapeHtml(p.city || "")}`, p.hue);
  globe.removeCursor(p.id);
});

let lastFeedAt = 0;
room.on("cell", ({ step, lane, cell, prev, by }) => {
  if (room.stats) hud.setStats(room.stats, room.humans().length);
  const hue = cell?.hue ?? prev?.hue ?? by?.hue ?? 200;
  const mine = by?.id === room.you?.id;
  if (!mine) {
    ring.burst(step, lane, hue, cell ? "place" : "remove");
    if (by) {
      labels.touch(by.id);
      broadcastVisual(by);
      const now = performance.now();
      if (!by.ghost && room.home && now - lastFeedAt > 1500) {
        lastFeedAt = now;
        hud.feedItem(`<span class="who">${escapeHtml(by.name)}</span> ${cell ? "wrote a note" : "erased a note"} ${pathLabel(by, room.home)}`, hue);
      }
    }
  }
});

room.on("pluck", (m) => {
  const origin = room.player(m.by) ?? { id: m.by, lat: m.lat, lon: m.lon, colo: m.colo, hue: m.hue };
  globe.pulse(m.lat, m.lon, m.hue, 0.8);
  labels.touch(m.by);
  // You hear a remote note when its light reaches you.
  broadcastVisual(origin, () => {
    conductor.pluck({ lane: m.lane, voice: m.voice, vel: m.vel, lat: m.lat, lon: m.lon });
  });
});

const cursorTimers = new Map<string, number>();
room.on("cursor", (m) => {
  const p = room.player(m.id);
  if (!p) return;
  globe.setCursor(m.id, m.lat, m.lon, p.hue);
  window.clearTimeout(cursorTimers.get(m.id));
  cursorTimers.set(m.id, window.setTimeout(() => globe.removeCursor(m.id), 4000));
});

room.on("stats", (s) => hud.setStats(s, room.humans().length));
room.on("rtt", () => labels.sync([...room.players.values()], room.you?.id ?? null, room.home));
room.on("narration", (n) => hud.narrate(n.text, n.by));
room.on("error", (msg) => hud.toast(escapeHtml(msg), 2500));

let lastBar = -1;
function onBar(bar: number, h: Harmony): void {
  lastBar = bar;
  room.prune(bar);
  const changed = hud.setHarmony(h, bar);
  if (changed) hud.toast(`☀ The planet turned — now in <b>${h.rootName} ${h.mode}</b>`, 4200);
  globe.setSun(h.sunLat, h.sunLon);
  if (room.home && bar % 2 === 0) globe.pulse(room.home.lat, room.home.lon, 28, 0.35);
}

// ───────────────────────────── local actions ─────────────────────────────

function toggle(hit: RingHit): void {
  const you = room.you;
  if (!you || playbackMode) return;
  const existing = room.cells.get(hit.step * LANES + hit.lane);
  ring.burst(hit.step, hit.lane, you.hue, existing ? "remove" : "place");
  if (!existing) conductor.pluck({ lane: hit.lane, voice: you.voice, vel: 0.35, lat: you.lat, lon: you.lon });
  net?.send({ t: "toggle", step: hit.step, lane: hit.lane });
  broadcastVisual(you);
  hideHint();
}

function pluck(lane: number, at?: LatLon): void {
  const you = room.you;
  if (!you) return;
  conductor.pluck({ lane, voice: you.voice, vel: 0.8, lat: you.lat, lon: you.lon });
  globe.pulse(you.lat, you.lon, you.hue, 1);
  if (at) globe.pulse(at.lat, at.lon, you.hue, 0.8);
  if (!playbackMode) {
    net?.send({ t: "pluck", lane, vel: 0.8 });
    broadcastVisual(you);
  }
  hideHint();
}

function laneForLatitude(lat: number): number {
  const t = (lat + 55) / 130;
  return Math.max(0, Math.min(LANES - 1, Math.round(t * (LANES - 1))));
}

// ───────────────────────────── pointer input ─────────────────────────────

interface Gesture {
  id: number;
  x: number;
  y: number;
  moved: boolean;
  onRing: boolean;
  paint: "add" | "remove" | null;
  painted: Set<number>;
}
let gesture: Gesture | null = null;
let lastPaint = 0;
let lastCursorSent = 0;

function overGlobe(x: number, y: number): boolean {
  return Math.hypot(x - layoutState.cx, y - layoutState.cy) <= layoutState.globeR;
}

ringCanvas.addEventListener("pointerdown", (e) => {
  ringCanvas.setPointerCapture(e.pointerId);
  const hit = ring.hit(e.offsetX, e.offsetY);
  gesture = { id: e.pointerId, x: e.offsetX, y: e.offsetY, moved: false, onRing: !!hit, paint: null, painted: new Set() };
  if (hit && !playbackMode) {
    const existing = room.cells.has(hit.step * LANES + hit.lane);
    gesture.paint = existing ? "remove" : "add";
    gesture.painted.add(hit.step * LANES + hit.lane);
    toggle(hit);
    lastPaint = performance.now();
  } else {
    globe.handlePointerDown(e);
  }
});

ringCanvas.addEventListener("pointermove", (e) => {
  const x = e.offsetX;
  const y = e.offsetY;
  if (gesture && gesture.id === e.pointerId) {
    if (Math.hypot(x - gesture.x, y - gesture.y) > 6) gesture.moved = true;
    if (gesture.onRing && gesture.paint) {
      const hit = ring.hit(x, y);
      if (hit) {
        const k = hit.step * LANES + hit.lane;
        const has = room.cells.has(k);
        const wants = gesture.paint === "add" ? !has : has;
        if (!gesture.painted.has(k) && wants && performance.now() - lastPaint > 110) {
          gesture.painted.add(k);
          lastPaint = performance.now();
          toggle(hit);
        }
      }
      ring.hover = hit;
      return;
    }
    globe.handlePointerMove(e);
    ringCanvas.classList.toggle("dragging", globe.isDragging());
    return;
  }
  ring.hover = ring.hit(x, y);
  ringCanvas.classList.toggle("over-globe", !ring.hover && overGlobe(x, y));
  // Share where we're looking with everyone else.
  if (!ring.hover && net && !playbackMode && performance.now() - lastCursorSent > 120) {
    const p = globe.pick(x, y);
    if (p) {
      lastCursorSent = performance.now();
      net.send({ t: "cursor", lat: +p.lat.toFixed(2), lon: +p.lon.toFixed(2) });
    }
  }
});

function endGesture(e: PointerEvent): void {
  if (!gesture || gesture.id !== e.pointerId) return;
  const g = gesture;
  gesture = null;
  ringCanvas.classList.remove("dragging");
  if (g.onRing) return;
  globe.handlePointerUp(e);
  if (!g.moved) {
    const p = globe.pick(e.offsetX, e.offsetY);
    if (p) pluck(laneForLatitude(p.lat), p);
  }
}
ringCanvas.addEventListener("pointerup", endGesture);
ringCanvas.addEventListener("pointercancel", endGesture);
ringCanvas.addEventListener("pointerleave", () => {
  if (!gesture) ring.hover = null;
});

// ───────────────────────────── keyboard ─────────────────────────────

const LANE_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8"];
const LANE_KEYS_ALT = ["a", "s", "d", "f", "g", "h", "j", "k"];
const heldKeys = new Set<string>();

// Keyboard access to the ring: focus it (Tab), move with arrows, Enter/Space toggles.
ringCanvas.addEventListener("keydown", (e) => {
  const h = ring.hover ?? { step: 0, lane: 3 };
  let next: RingHit | null = null;
  if (e.key === "ArrowRight") next = { step: (h.step + 1) % 16, lane: h.lane };
  else if (e.key === "ArrowLeft") next = { step: (h.step + 15) % 16, lane: h.lane };
  else if (e.key === "ArrowUp") next = { step: h.step, lane: Math.min(LANES - 1, h.lane + 1) };
  else if (e.key === "ArrowDown") next = { step: h.step, lane: Math.max(0, h.lane - 1) };
  else if (e.key === "Enter" || (e.key === " " && !introVisible())) {
    e.preventDefault();
    e.stopPropagation();
    if (!engine.started) void startAudio();
    toggle(h);
    ring.hover = h;
    return;
  }
  if (next) {
    e.preventDefault();
    ring.hover = next;
  }
});
ringCanvas.addEventListener("focus", () => {
  if (!ring.hover) ring.hover = { step: 0, lane: 3 };
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement || e.metaKey || e.ctrlKey || e.altKey) return;
  const k = e.key.toLowerCase();
  if (heldKeys.has(k)) return;
  heldKeys.add(k);
  let lane = LANE_KEYS.indexOf(k);
  if (lane < 0 && !introVisible()) lane = LANE_KEYS_ALT.indexOf(k);
  if (lane >= 0 && introDone) {
    if (!engine.started) void startAudio();
    pluck(lane);
    return;
  }
  if (k === "escape") closePanels();
  else if (k === "m") toggleMute();
  else if (k === "c" && !playbackMode) void doCapture();
  else if (k === "?" || k === "/") showHelp();
  else if (k === "k") toggleSky();
  else if (k === " " && introVisible()) enter(true);
});
window.addEventListener("keyup", (e) => heldKeys.delete(e.key.toLowerCase()));
window.addEventListener("blur", () => heldKeys.clear());

// The "g" key was taken by the alternate lane row, so globe view uses Tab-free shift+G too.
window.addEventListener("keydown", (e) => {
  if (e.key === "G") setGlobeMode(!layoutState.globeMode);
});

// ───────────────────────────── controls ─────────────────────────────

const safeStorage = {
  get(k: string): string | null {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string): void {
    try {
      localStorage.setItem(k, v);
    } catch {
      // Storage unavailable (private mode): fine.
    }
  },
};

let muted = false;
async function startAudio(): Promise<void> {
  await engine.start();
  engine.setMuted(muted);
  engine.setMasterVolume(0.9);
  conductor.resetHarmonyCache();
}

function toggleMute(): void {
  if (!engine.started) {
    muted = false;
    void startAudio();
  } else {
    muted = !muted;
    engine.setMuted(muted);
  }
  $("btn-mute").setAttribute("aria-pressed", String(muted || !engine.started));
}

$("btn-mute").addEventListener("click", toggleMute);
$("btn-globe").addEventListener("click", () => setGlobeMode(!layoutState.globeMode));
$("btn-help").addEventListener("click", showHelp);
$("btn-sky").addEventListener("click", toggleSky);
$("btn-capture").addEventListener("click", () => void doCapture());
$("btn-room").addEventListener("click", () => {
  const words = ["aurora", "comet", "harbor", "lantern", "meridian", "nebula", "orbit", "tide", "zenith", "ember"];
  const name = `${words[Math.floor(Math.random() * words.length)]}-${Math.random().toString(36).slice(2, 6)}`;
  location.href = `/r/${name}`;
});
$("you-name").addEventListener("click", () => {
  if (!room.you || playbackMode) return;
  const next = window.prompt("What should the planet call you?", room.you.name);
  if (!next) return;
  const name = sanitizeName(next);
  if (!name) return;
  safeStorage.set("anycast.name", name);
  net?.send({ t: "rename", name });
});

async function doCapture(): Promise<void> {
  if (playbackMode) return;
  if (!engine.started) await startAudio();
  await captureMoment(room.name, engine, hud, $("btn-capture"));
}

function showHelp(): void {
  $("help").classList.remove("hidden");
}

function toggleSky(): void {
  const panel = $("sky-panel");
  const opening = panel.classList.contains("hidden");
  panel.classList.toggle("hidden", !opening);
  if (opening) void loadSky($("sky-list"));
}

function closePanels(): void {
  $("help").classList.add("hidden");
  $("sky-panel").classList.add("hidden");
}

for (const btn of document.querySelectorAll<HTMLElement>("[data-close]")) {
  btn.addEventListener("click", closePanels);
}
$("help").addEventListener("click", (e) => {
  if (e.target === $("help")) closePanels();
});

// ───────────────────────────── intro ─────────────────────────────

let introDone = false;
function introVisible(): boolean {
  return !$("intro").classList.contains("hidden");
}

function enter(withSound: boolean): void {
  if (introDone) return;
  introDone = true;
  $("intro").classList.add("hidden");
  if (withSound) void startAudio();
  else $("btn-mute").setAttribute("aria-pressed", "true");
  safeStorage.set("anycast.visited", "1");
  if (room.you) globe.focus(room.you.lat, room.you.lon - 20, 1800);
}
$("btn-enter").addEventListener("click", () => enter(true));
$("btn-listen").addEventListener("click", () => enter(false));

let hintHidden = false;
function hideHint(): void {
  if (hintHidden) return;
  hintHidden = true;
  window.setTimeout(() => $("keys-hint").classList.add("gone"), 2500);
}

// ───────────────────────────── frame loop ─────────────────────────────

let lastHudUpdate = 0;
let lastRttShared = -Infinity;
let energy = 0;
function frame(): void {
  const now = performance.now();
  // Ease between ring view and globe view.
  const target = layoutState.globeMode ? 1 : 0;
  if (Math.abs(layoutState.t - target) > 0.001) {
    layoutState.t += (target - layoutState.t) * 0.12;
    if (Math.abs(layoutState.t - target) < 0.002) layoutState.t = target;
    applyLayout();
  }
  conductor.flushVisuals();
  const level = engine.started ? engine.getLevel() : 0;
  energy += (level - energy) * 0.2;
  globe.energy = Math.min(1, energy * 2.2);
  globe.frame(now);

  const serverNow = net ? net.serverNow() : localNow();
  const bar = barIndex(serverNow, room.epoch);
  const h = conductor.lastHarmony ?? conductor.harmony(bar);
  ring.draw({
    now,
    serverNow,
    epoch: room.epoch,
    bar,
    cells: room.cells,
    youId: room.you?.id ?? null,
    harmony: h,
    voice: room.you?.voice ?? "pluck",
    energy: globe.energy,
  });
  labels.update(globe, false);

  if (now - lastHudUpdate > 500) {
    lastHudUpdate = now;
    if (lastBar < 0) {
      hud.setHarmony(h, bar);
      globe.setSun(h.sunLat, h.sunLon);
    }
    if (room.you && room.home && net) {
      hud.setLatency(net.isOffline ? NaN : net.clock.typicalRtt, theoreticalRttMs(room.you, room.home));
      // Share our measured round trip so others can see it on the globe.
      const rtt = net.clock.typicalRtt;
      if (!net.isOffline && Number.isFinite(rtt) && now - lastRttShared > 6000) {
        lastRttShared = now;
        net.send({ t: "rtt", ms: Math.round(rtt) });
      }
    }
  }
  requestAnimationFrame(frame);
}

// ───────────────────────────── boot ─────────────────────────────

async function bootLive(roomName: string): Promise<void> {
  net = new Net(roomName, { forceOffline: params.has("offline") });
  net.on("message", (m) => room.apply(m));
  net.on("status", (s) => hud.setStatus(s));
  hud.setStatus("connecting");
  hud.setRoom(roomName);
  net.connect();
}

async function bootConstellation(id: string): Promise<void> {
  playbackMode = true;
  document.body.classList.add("playback-mode");
  hud.setStatus("offline");
  $("playback").classList.remove("hidden");
  const c = await fetchConstellation(id);
  if (!c) {
    $("pb-title").textContent = "This constellation has faded from the sky.";
    $("pb-meta").textContent = "It may have been too old, or the link is wrong.";
    $("intro").classList.add("hidden");
    introDone = true;
    return;
  }
  document.title = `${c.title} — ANYCAST`;
  $("pb-title").textContent = `“${c.title}”`;
  const when = new Date(c.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  $("pb-meta").textContent = `${c.humans} ${c.humans === 1 ? "person" : "people"} · ${c.places.join(" · ")} · ${when}${c.titleBy === "ai" ? " · titled by Workers AI" : ""}`;
  $("intro-eyebrow").textContent = "a captured moment";
  $("intro-title").innerHTML = `<span class="grad">“${escapeHtml(c.title)}”</span>`;
  $("btn-enter").innerHTML = `Listen <span aria-hidden="true">→</span>`;
  const bar = barIndex(Date.now(), 0);
  conductor.harmonyOverride = { root: c.root, mode: c.mode };
  const listener: PlayerInfo = {
    id: "__listener",
    name: "You",
    hue: 200,
    voice: "glass",
    lat: c.home.lat,
    lon: c.home.lon,
    city: "",
    country: "",
    continent: "",
    colo: c.home.colo,
    ghost: true,
    joinedAt: Date.now(),
  };
  room.apply({
    t: "welcome",
    room: c.room,
    you: listener,
    players: c.players,
    cells: c.cells.map((cell) => ({ ...cell, born: bar, ttl: 1e9 })),
    epoch: 0,
    now: Date.now(),
    home: c.home,
    stats: { online: c.humans, notesEver: c.noteCount, visitorsEver: c.humans, countries: 0, colos: 0, byColo: {} },
  });
  const firstHuman = c.players.find((p) => !p.ghost) ?? c.players[0];
  if (firstHuman) globe.focus(firstHuman.lat, firstHuman.lon - 20, 2000);
  void fetch(`/api/constellations/${c.id}/play`, { method: "POST" }).catch(() => undefined);
  if (c.hasRecording) {
    const btn = $("pb-recording");
    const audio = $<HTMLAudioElement>("pb-audio");
    btn.classList.remove("hidden");
    audio.src = `/api/constellations/${c.id}/recording`;
    btn.addEventListener("click", () => {
      if (audio.paused) {
        if (engine.started) engine.setMuted(true);
        void audio.play();
        btn.textContent = "■ Stop recording";
      } else {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    audio.addEventListener("pause", () => {
      btn.textContent = "▶ Original recording";
      if (engine.started) engine.setMuted(muted);
    });
    audio.addEventListener("ended", () => audio.dispatchEvent(new Event("pause")));
  }
}

// Initial sun so the globe is lit correctly before the first bar.
{
  const s = subsolarPoint(Date.now());
  globe.setSun(s.lat, s.lon);
}

if (appRoute.kind === "live") {
  void bootLive(appRoute.room);
  void paintSkyConstellations($<HTMLCanvasElement>("sky-stars"));
}
else void bootConstellation(appRoute.id);

if (params.has("nointro")) enter(false);

// Kiosk: a spectator view for big screens. The camera follows the music.
if (params.has("kiosk")) {
  document.body.classList.add("kiosk");
  enter(false);
  setGlobeMode(true);
  const btn = document.createElement("button");
  btn.className = "kiosk-sound";
  btn.textContent = "♪ Click for sound";
  btn.addEventListener("click", () => {
    void startAudio();
    $("btn-mute").setAttribute("aria-pressed", "false");
    btn.remove();
  });
  document.body.appendChild(btn);
  let lastFocus = 0;
  const follow = (lat: number, lon: number) => {
    const now = performance.now();
    if (now - lastFocus < 12_000) return;
    lastFocus = now;
    globe.focus(lat, lon - 15, 3000);
  };
  room.on("cell", ({ by }) => by && !by.ghost && follow(by.lat, by.lon));
  room.on("pluck", (m) => follow(m.lat, m.lon));
  room.on("join", (p) => !p.ghost && follow(p.lat, p.lon));
}

requestAnimationFrame(frame);

// Debug handle for the console and for automated screenshots.
(window as unknown as { anycast: unknown }).anycast = { globe, ring, room, engine, conductor, get net() { return net; }, setGlobeMode };
